import React, {useContext, useRef, useState} from "react"
import annualReportSteps from "../../constants/annualReportSteps";
import Form from "components/Form";
import PropTypes from "prop-types";
import Loading from "components/Loading";
import NoticeGlossaryInput from "components/complex/nationalNotice/components/inputs/NoticeGlossaryInput";
import NoticeTextInput from "components/complex/nationalNotice/components/inputs/NoticeTextInput";
import FormTranslationContext from "context/FormTranslationContext";
import Typography from "components/Typography";
import Tr from "translations/Translations";
import BoldTypography from "components/BoldTypography";
import GridBlock from "components/GridBlock";
import WorkableButton from "components/button/WorkableButton";
import AnnualReportsService from "services/AnnualReportsService";
import {useParams} from "react-router";
import TypographyWithIcon from "components/TypographyWithIcon";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import useSnackbar from "hooks/useSnackbar";
import annualReportValidation from "../../constants/annualReportValidation";
import {Collapse} from "@material-ui/core";
import getObjectValue from "libs/getObjectValue";
import scrollTo from "libs/scrollTo";
import AnnualReportStatus, {annualReportStatusTr} from "../../constants/AnnualReportStatus";

function AnnualReportPurchaserForm(
    {
        initValues,
        classes,
        disabled,
        setFrom
    }
) {
    const params = useParams();
    const [error, setError] = useState();
    const {getDict, formTr} = useContext(FormTranslationContext);
    const {enqueueSuccessSnackbar} = useSnackbar();
    const submitSemaphore = useRef(false);

    const onSubmit = (values, actions) => {
        submitSemaphore.current = false;
        AnnualReportsService.saveAnnualReportForm(params.id, annualReportSteps.purchaser, values).then((resp) => {
            if (resp) {
                if (resp.success) {
                    enqueueSuccessSnackbar(Tr("saved"))
                    actions.resetForm();
                } else {
                    if (resp.errors instanceof Object) {
                        for (let key of Object.keys(resp.errors)) {
                            setError(formTr(`mmia.${annualReportSteps.purchaser}.${key}`) + ": " + resp.errors[key])
                            break;
                        }
                    }
                }

            }
        })
    }

    const handleSubmit = (formikProps, complete) => {
        setError(null)
        formikProps.setFieldValue('complete', complete);
        submitSemaphore.current = true;
        setTimeout((formikProps) => formikProps.submitForm(), 0, formikProps)
        setTimeout((formId, submitSemaphore) => submitSemaphore.current && scrollTo("#" + formId + " .MuiFormHelperText-root.Mui-error", 200), 200, annualReportSteps.purchaser, submitSemaphore);
    }

    if (!initValues) return <Loading/>
    return (<Form
            htmlFormId={annualReportSteps.purchaser}
            initialValues={{
                ...initValues
            }}
            validationSchema={annualReportValidation.purchaser}
            validateOnMount
            onSubmit={onSubmit}
        >{(formikProps) => {
            setFrom(formikProps)
            return <>
                <GridBlock justify="space-between" className={classes.variantRow}>
                    <GridBlock alignItems="flex-end">
                        <BoldTypography variant="h5" className={classes.marginRight}>{formTr("mmia.purchaser")}</BoldTypography>
                        {!initValues.complete && <Typography color="error">({annualReportStatusTr(AnnualReportStatus.DRAFT)})</Typography>}
                    </GridBlock>
                    <>

                        <WorkableButton
                            className={classes.marginRight}
                            disabled={disabled || !formikProps.dirty}
                            variant="outlined"
                            onClick={() => formikProps.resetForm()}>
                            {Tr('discard_changes')}
                        </WorkableButton>
                        <WorkableButton
                            className={classes.marginRight}
                            disabled={disabled}
                            onClick={() => handleSubmit(formikProps, false)}
                            variant="outlined">
                            {Tr('save_draft_version')}
                        </WorkableButton>
                        <WorkableButton
                            disabled={disabled}
                            onClick={() => handleSubmit(formikProps, true)}>
                            {Tr('save')}
                        </WorkableButton>
                    </>
                </GridBlock>

                <BoldTypography variant="h6">{formTr("mmia.purchaserS1")}</BoldTypography>
                <br/>
                {initValues.dataLoaded && <BoldTypography color="primary">{formTr("mmia.purchaser.dataLoaded")}<br/><br/></BoldTypography>}

                {!!error && <TypographyWithIcon Icon={ErrorOutlineIcon} color='error'>{error}</TypographyWithIcon>}
                <NoticeTextInput
                    id="purchaserName"
                    label="mmia.purchaser.purchaserName"
                    formikProps={formikProps}
                    disabled
                />
                <NoticeTextInput
                    id="purchaserPartName"
                    label="mmia.purchaser.purchaserPartName"
                    formikProps={formikProps}
                    disabled={initValues.dataLoaded || disabled}
                />

                <NoticeTextInput
                    id="purchaserIdentifier"
                    label="mmia.purchaser.purchaserIdentifier"
                    formikProps={formikProps}
                    disabled={initValues.dataLoaded || disabled}
                />
                <NoticeGlossaryInput
                    id="purchaserType"
                    label="mmia.purchaser.purchaserType"
                    formikProps={formikProps}
                    items={getDict("SL.MO.013")}
                    disabled={initValues.dataLoaded || disabled}
                />
                <Collapse in={getObjectValue("purchaserType", formikProps.values, null) === "1.5"}>
                    <NoticeTextInput
                        id="purchaserOtherType"
                        label="mmia.purchaser.purchaserOtherType"
                        formikProps={formikProps}
                        disabled={initValues.dataLoaded || disabled}
                    />
                </Collapse>

                <NoticeGlossaryInput
                    id="purchaserActivityType"
                    label="mmia.purchaser.purchaserActivityType"
                    formikProps={formikProps}
                    items={getDict("SL.MMiA.065")}
                    disabled={disabled}
                />

                <br/>
                <BoldTypography variant="h6">{formTr("mmia.purchaserS2")}</BoldTypography>
                <br/>
                <NoticeTextInput
                    id="purchaserStreet"
                    label="mmia.purchaser.purchaserStreet"
                    formikProps={formikProps}
                    disabled={initValues.dataLoaded || disabled}
                />
                <NoticeTextInput
                    id="purchaserCity"
                    label="mmia.purchaser.purchaserCity"
                    formikProps={formikProps}
                    disabled={initValues.dataLoaded || disabled}
                />
                <NoticeTextInput
                    id="purchaserCode"
                    label="mmia.purchaser.purchaserCode"
                    formikProps={formikProps}
                    disabled={initValues.dataLoaded || disabled}
                />
                <NoticeGlossaryInput
                    id="purchaserProvince"
                    label="mmia.purchaser.purchaserProvince"
                    formikProps={formikProps}
                    items={getDict("MMIA.SL.MT.007")}
                    disabled={initValues.dataLoaded || disabled}
                />
                <NoticeGlossaryInput
                    id="purchaserCountry"
                    label="mmia.purchaser.purchaserCountry"
                    formikProps={formikProps}
                    items={getDict("SL.MT.005")}
                    disabled={initValues.dataLoaded || disabled}
                />
                <NoticeTextInput
                    id="purchaserUrl"
                    label="mmia.purchaser.purchaserUrl"
                    formikProps={formikProps}
                    disabled={initValues.dataLoaded || disabled}
                />
                <NoticeTextInput
                    id="purchaserEmail"
                    label="mmia.purchaser.purchaserEmail"
                    formikProps={formikProps}
                    disabled={initValues.dataLoaded || disabled}
                />
                <NoticeTextInput
                    id="purchaserPhone"
                    label="mmia.purchaser.purchaserPhone"
                    formikProps={formikProps}
                    disabled={initValues.dataLoaded || disabled}
                />

                <BoldTypography variant="h6">{formTr("mmia.purchaserS3")}</BoldTypography>

                <NoticeTextInput
                    id="issueNumber"
                    label="mmia.purchaser.issueNumber"
                    formikProps={formikProps}
                    disabled={disabled}
                />
            </>
        }}
        </Form>
    )


}

AnnualReportPurchaserForm.prototype = {
    initValues: PropTypes.object,
    setFrom: PropTypes.func.isRequired,
    disabled: PropTypes.bool.isRequired,
    classes: PropTypes.object.isRequired
}

export default AnnualReportPurchaserForm;